import React, { useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';

import { FilterObject, SortBy, SortOrder } from '../../../../types/api';
import { SearchFilterKey } from '../../../../store/types';
import useStore from '../../../../store';

import Text from '../../../../components/Text';
import Stack from '../../../../components/Stack';
import FilterSelect from '../../../../components/FilterSelect';
import RangeSlider from '../../../../components/RangeSlider';
import Button from '../../../../components/Button';
import Input from '../../../../components/Input';

import {
  ExpandedFilterWrapper,
  FilterMeta,
  FilterContent,
  FilterWrapper,
  AddressFilterWrapper
} from './Filter.style';
import Select, { SelectOption } from '@/components/Select';

const Filter = () => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const updateFilter = useStore(state => state.search.updateFilter);
  const updateQuery = useStore(state => state.search.updateQuery);
  const fetchResults = useStore(state => state.search.fetchResults);
  const filter = useStore(state => state.search.filter);
  const expanded = useStore(state => state.search.expanded);
  const updateExpanded = useStore(state => state.search.updateExpanded);
  const updateSortBy = useStore(state => state.search.updateSortBy);
  const sortBy = useStore(state => state.search.sortBy);
  const resetFilter = useStore(state => state.search.resetFilter);
  const facets = useStore(state => state.search.facets);
  const count = useStore(state => state.search.count);
  const current = useStore(state => state.search.results.length);
  const loading = useStore(state => state.search.loading || state.search.loadingMore);

  const hasFilters =
    filter.query !== '' ||
    (filter.deportedTo && filter.deportedTo.length > 0) ||
    (filter.placeOfBirth && filter.placeOfBirth.length > 0) ||
    (filter.placeOfDeath && filter.placeOfDeath.length > 0) ||
    (filter.address && filter.address.length > 0) ||
    (filter.houseNumber && filter.houseNumber.length > 0) ||
    (filter.school && filter.school.length > 0) ||
    filter.birthDate ||
    filter.deathDate;

  const sortByKeys: { label: string; value: string }[] = [
    { value: 'relevance', label: t('form.labels.relevance') },
    { value: 'name', label: t('biography.name') },
    { value: 'birthDate', label: t('biography.birthDate') },
    { value: 'deathDate', label: t('biography.deathDate') },
    { value: 'deportedTo', label: t('biography.deportedTo') },
    { value: 'deportationDate', label: t('biography.deportationDate') }
  ];

  const handleSearch = async () => {
    try {
      await fetchResults(router.locale);
    } catch (error) {
      console.log('Error while searching');
    }
  };

  const handleFilterUpdate = <T extends SearchFilterKey, R extends FilterObject[T]>(
    key: T,
    value: R
  ) => {
    if (timeout.current) clearTimeout(timeout.current);
    updateFilter(key, value);
    timeout.current = setTimeout(handleSearch, 500);
  };

  const handleQueryUpdate = (value: string) => {
    if (timeout.current) clearTimeout(timeout.current);
    updateQuery(value);
    timeout.current = setTimeout(handleSearch, 500);
  };

  return (
    <FilterWrapper>
      <form
        noValidate
        onSubmit={e => {
          e.preventDefault();
          if (timeout.current) clearTimeout(timeout.current);
          handleSearch();
        }}
      >
        <Stack gap="s">
          <Input
            id="query"
            onChange={e => handleQueryUpdate(e.target.value)}
            value={filter.query}
            label={t('form.searchValue.label')}
            placeholder={t('form.searchValue.placeholder')}
          />

          <FilterContent>
            <FilterMeta>
              <Text variant="copy-small">
                {current}/{count} {t('form.labels.results')}
              </Text>

              <div>
                {hasFilters && (
                  <Button
                    variant="tertiary"
                    onClick={resetFilter}
                    iconPosition="after"
                    label={t('labels.reset')}
                  />
                )}

                <Button
                  variant="tertiary"
                  onClick={updateExpanded}
                  iconPosition="after"
                  label={t(expanded ? 'form.labels.collapse' : 'form.labels.expand')}
                />

                <Button
                  type="submit"
                  label={t('form.labels.search')}
                  icon="Search"
                  iconPosition="after"
                  disabled={!hasFilters || loading}
                />
              </div>
            </FilterMeta>

            {expanded && (
              <ExpandedFilterWrapper>
                <RangeSlider
                  id="birthDate"
                  label={t('form.birthDate.label')}
                  value={
                    filter.birthDate ? [filter.birthDate.from, filter.birthDate.to] : undefined
                  }
                  min={facets.birthDate.min}
                  max={facets.birthDate.max}
                  onChange={val => handleFilterUpdate('birthDate', { from: val[0], to: val[1] })}
                />

                <RangeSlider
                  id="deathDate"
                  label={t('form.deathDate.label')}
                  value={
                    filter.deathDate ? [filter.deathDate.from, filter.deathDate.to] : undefined
                  }
                  min={facets.deathDate.min}
                  max={facets.deathDate.max}
                  onChange={val => handleFilterUpdate('deathDate', { from: val[0], to: val[1] })}
                />

                <AddressFilterWrapper>
                  <FilterSelect
                    label={t('form.address.label')}
                    placeholder={t('form.address.placeholder')}
                    name="address"
                    isMulti={false}
                    value={filter.address.map(i => ({ value: i, label: i }))[0] || null}
                    onChange={item => {
                      if (item) {
                        handleFilterUpdate('address', [item.value]);
                      } else {
                        handleFilterUpdate('address', []);
                        handleFilterUpdate('houseNumber', []);
                      }
                    }}
                    options={facets.address
                      .map(i => ({ value: i.key, label: i.key }))
                      .sort((a, b) => (a.label > b.label ? 1 : -1))}
                  />

                  {filter.address.length > 0 && (
                    <FilterSelect
                      disabled={filter.address.length === 0}
                      label={t('form.houseNumber.label')}
                      placeholder={t('form.houseNumber.placeholder')}
                      name="houseNumber"
                      isMulti={false}
                      value={filter.houseNumber.map(i => ({ value: i, label: i }))[0] || null}
                      onChange={item => handleFilterUpdate('houseNumber', item ? [item.value] : [])}
                      options={(facets.houseNumber || [])
                        .map(i => ({ value: i.key, label: i.key }))
                        .sort((a, b) => (a.label > b.label ? 1 : -1))}
                    />
                  )}
                </AddressFilterWrapper>

                <FilterSelect
                  label={t('form.placeOfBirth.label')}
                  placeholder={t('form.placeOfBirth.placeholder')}
                  name="placeOfBirth"
                  value={filter.placeOfBirth.map(i => ({ value: i, label: i }))}
                  onChange={items =>
                    handleFilterUpdate(
                      'placeOfBirth',
                      items.map((i: any) => i.value)
                    )
                  }
                  options={facets.placeOfBirth
                    .map(i => ({ value: i.key, label: i.key }))
                    .sort((a, b) => (a.label > b.label ? 1 : -1))}
                />

                <FilterSelect
                  label={t('form.placeOfDeath.label')}
                  placeholder={t('form.placeOfDeath.placeholder')}
                  name="placeOfDeath"
                  value={filter.placeOfDeath.map(i => ({ value: i, label: i }))}
                  onChange={items =>
                    handleFilterUpdate(
                      'placeOfDeath',
                      items.map((i: any) => i.value)
                    )
                  }
                  options={facets.placeOfDeath
                    .map(i => ({ value: i.key, label: i.key }))
                    .sort((a, b) => (a.label > b.label ? 1 : -1))}
                />

                <FilterSelect
                  label={t('form.deportedTo.label')}
                  placeholder={t('form.deportedTo.placeholder')}
                  name="deportedTo"
                  value={filter.deportedTo.map(i => ({ value: i, label: i }))}
                  onChange={items =>
                    handleFilterUpdate(
                      'deportedTo',
                      items.map((i: any) => i.value)
                    )
                  }
                  options={facets.deportedTo
                    .map(i => ({ value: i.key, label: i.key }))
                    .sort((a, b) => (a.label > b.label ? 1 : -1))}
                />

                <FilterSelect
                  label={t('form.school.label')}
                  placeholder={t('form.school.placeholder')}
                  name="school"
                  value={filter.school.map(i => ({ value: i, label: i }))}
                  onChange={items =>
                    handleFilterUpdate(
                      'school',
                      items.map((i: any) => i.value)
                    )
                  }
                  options={facets.school
                    .map(i => ({ value: i.key, label: i.key }))
                    .sort((a, b) => (a.label > b.label ? 1 : -1))}
                />

                <FilterSelect
                  value={sortByKeys.find(i => i.value === sortBy)}
                  name="sortBy"
                  isMulti={false}
                  isClearable={false}
                  isSearchable={false}
                  options={sortByKeys}
                  label={t('form.labels.sortBy')}
                  onChange={item => updateSortBy(item.value as SortBy)}
                />
              </ExpandedFilterWrapper>
            )}
          </FilterContent>
        </Stack>
      </form>
    </FilterWrapper>
  );
};

export default React.memo(Filter);
